import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import MDBox from "components/MDBox";
import { accessFromUser } from "helpers/permissions";
import { readUserSession } from "services/access";
import { fetchSaldoEfi, type SaldoEfiApiResponse } from "services/saldoEfi";
import { userCanViewSaldoEfi as checkSaldoEfiPermission } from "./saldoEfiPermissions";
import Skeleton from "@mui/material/Skeleton";

const brl = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

function readUserAccessFromStorage() {
  return accessFromUser(readUserSession());
}

function parseMoneyString(value: string | undefined): number {
  if (value == null || value === "") return NaN;
  const n = Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

export type EfiAccountBalanceCardProps = {
  /** Se omitido, usa dados de acesso do usuário em `localStorage`. */
  roles?: string[];
  permissions?: string[];
  isSuperAdmin?: boolean;
  /** Estilo do container (ex.: `mb: 3`). */
  sx?: SxProps<Theme>;
  /** Versão compacta (uma linha, menos padding). */
  compact?: boolean;
};

/**
 * Exibe o saldo da conta Efí (GET /saldo-efi). Não renderiza nada se o usuário não tiver permissão.
 * Reutilizável em qualquer rota do dashboard.
 */
export default function EfiAccountBalanceCard({
  roles: rolesProp,
  permissions: permissionsProp,
  isSuperAdmin: isSuperAdminProp,
  sx,
  compact = false,
}: EfiAccountBalanceCardProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const access = useMemo(() => {
    const stored = readUserAccessFromStorage();
    return {
      roles: rolesProp ?? stored.roles,
      permissions: permissionsProp ?? stored.permissions,
      isSuperAdmin: isSuperAdminProp ?? stored.isSuperAdmin,
    };
  }, [rolesProp, permissionsProp, isSuperAdminProp]);

  const allowed = useMemo(() => checkSaldoEfiPermission(access), [access]);

  const [data, setData] = useState<SaldoEfiApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSaldoEfi();
      setData(res);
    } catch (e) {
      setData(null);
      setError(e instanceof Error ? e.message : "Erro ao carregar saldo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    void load();
  }, [allowed, load]);

  if (!allowed) {
    return null;
  }

  const saldoNum = parseMoneyString(data?.saldo);
  const bloqueioTotal = parseMoneyString(data?.bloqueios?.total);
  const temBloqueio = Number.isFinite(bloqueioTotal) && bloqueioTotal > 0;

  const accent =
    mode === "dark"
      ? alpha("#a78bfa", 0.85)
      : (theme.palette.secondary?.main ?? "#7c3aed");
  const borderSoft =
    mode === "dark" ? alpha("#a78bfa", 0.35) : alpha("#7c3aed", 0.22);
  const wash =
    mode === "dark" ? alpha("#a78bfa", 0.08) : alpha("#7c3aed", 0.06);

  return (
    <MDBox sx={sx}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          background: `linear-gradient(135deg, ${wash} 0%, transparent 55%)`,
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <MDBox
          p={compact ? 2 : 2.5}
          display="flex"
          alignItems={compact ? "center" : "flex-start"}
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Stack
            direction={compact ? "row" : "column"}
            spacing={compact ? 2 : 1}
            alignItems={compact ? "center" : "flex-start"}
            flex={1}
            minWidth={0}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              minWidth={0}
            >
              <AccountBalanceWalletOutlinedIcon
                fontSize={compact ? "medium" : "large"}
                sx={{
                  color: accent,
                  flexShrink: 0,
                }}
              />
              <div>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  letterSpacing={0.3}
                  display="block"
                >
                  Saldo na conta Efí
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ opacity: 0.85 }}
                >
                  Disponível no gateway de pagamentos
                </Typography>
              </div>
            </Stack>

            {!compact ? (
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ color: accent, mt: 0.5 }}
              >
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={100}
                    height={36}
                    animation="pulse"
                  />
                ) : error ? (
                  "—"
                ) : Number.isFinite(saldoNum) ? (
                  brl.format(saldoNum)
                ) : (
                  "—"
                )}
              </Typography>
            ) : (
              <Typography variant="h6" fontWeight="bold" sx={{ color: accent }}>
                {loading ? (
                  <Skeleton
                    variant="text"
                    width={100}
                    height={36}
                    animation="pulse"
                  />
                ) : error ? (
                  "—"
                ) : Number.isFinite(saldoNum) ? (
                  brl.format(saldoNum)
                ) : (
                  "—"
                )}
              </Typography>
            )}

            {!loading && error ? (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            ) : null}

            {!loading && !error && temBloqueio ? (
              <Typography variant="caption" color="warning.main">
                Valores bloqueados (MED/judicial): {brl.format(bloqueioTotal)}
              </Typography>
            ) : null}
          </Stack>

          <Tooltip title="Atualizar saldo">
            <span>
              <IconButton
                size="small"
                onClick={() => void load()}
                disabled={loading}
                aria-label="Atualizar saldo Efí"
                sx={{ border: `1px solid ${borderSoft}` }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    animation: loading
                      ? "efi-spin 0.8s linear infinite"
                      : "none",
                    "@keyframes efi-spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </MDBox>
      </Card>
    </MDBox>
  );
}
